import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Flex,
  Spinner,
  Text,
  toaster,
} from '@optiaxiom/react';
import { useDecision } from '@optimizely/react-sdk';

import { workflowAgentAtom } from '@frontend/atoms';
import { EmptyPage } from '@frontend/components';
import { AgentTabTable } from '@frontend/components/agent-tab-table';
import { MarkdownText } from '@frontend/components/MarkdownText';
import { useInstanceId, useOpalUser } from '@frontend/hooks';
import { UnifiedAgent } from '@frontend/types/agent';
import { copyJsonToClipboard } from '@frontend/utils/copyToClipboard';

import { usePushToAgentDirectory } from '../hooks/useAgentDirectory';
import { AgentStatus, useAgentsUrlParams } from '../hooks/useAgentsUrlParams';
import { useGetAgentsWithPagination } from '../hooks/useGetAgentsWithPagination';
import { exportSpecializedAgent } from '../services/specialized-agent';
import { exportWorkflowAgent } from '../services/workflow-agent';

import { AgentSearchInput } from './shared/AgentSearchInput';
import { styles } from './AgentTab.css';
import { PublishAgentSidebar } from './PublishAgentSidebar';

export function AgentTab() {
  const { opalUser } = useOpalUser();
  const navigate = useNavigate();
  const [, dispatch] = useAtom(workflowAgentAtom);
  const [workflowAgentFeature] = useDecision('workflow-agent');
  const [agentDirectoryPublisherFeature] = useDecision('agentdirectorypublisher');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [exportData, setExportData] = useState<unknown>(null);
  const [selectedAgent, setSelectedAgent] = useState<UnifiedAgent | null>(null);
  const [isPublishSidebarOpen, setIsPublishSidebarOpen] = useState(false);
  const [selectedAgentForPublish, setSelectedAgentForPublish] = useState<UnifiedAgent | null>(null);

  const { instanceId } = useInstanceId();
  // Push to directory hook
  const { mutate: pushToDirectory, isPending: isPushingToDirectory } = usePushToAgentDirectory();
  // URL parameter management
  const { params, updateParams } = useAgentsUrlParams();
  const { search, page, pageSize, type, status } = params;

  const currentHref = location.pathname + location.search + location.hash;
  // Determine effective agent type based on feature flag
  const effectiveAgentType = workflowAgentFeature.enabled ? type : 'specialized';

  // API call with URL parameters
  const {
    data: agentsResponse,
    isPending,
    error,
  } = useGetAgentsWithPagination({
    instanceId: instanceId || '',
    page,
    pageSize,
    searchQuery: search,
    agentType: effectiveAgentType,
    status,
  });

  // Event handlers that update URL
  const handleSearchChange = useCallback(
    (searchQuery: string) => {
      updateParams({ search: searchQuery });
    },
    [updateParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateParams({ page: newPage });
    },
    [updateParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateParams({ pageSize: newPageSize });
    },
    [updateParams]
  );

  const handleAgentTypeChange = useCallback(
    (agentType?: 'specialized' | 'workflow') => {
      updateParams({ type: agentType });
    },
    [updateParams]
  );

  const handleStatusChange = useCallback(
    (status?: AgentStatus) => {
      updateParams({ status });
    },
    [updateParams]
  );

  const navigateWithBackHref = useCallback(
    (path: string) => {
      navigate(`${path}?backHref=${encodeURIComponent(currentHref)}`);
    },
    [currentHref, navigate]
  );

  const onClickAgentListItem = useCallback(
    (agent: UnifiedAgent) => {
      if (agent.agent_type === 'specialized') {
        navigateWithBackHref(`/agents/specialized/${agent.id}`);
      } else if (agent.agent_type === 'workflow') {
        dispatch({ type: 'RESET' });
        navigateWithBackHref(`/agents/workflow/edit/${agent.id}`);
      }
    },
    [dispatch, navigateWithBackHref]
  );

  const handleCopyAgent = useCallback(
    (agent: UnifiedAgent) => {
      if (agent.agent_type === 'specialized') {
        navigateWithBackHref(`/agents/specialized/${agent.id}/copy`);
      } else if (agent.agent_type === 'workflow') {
        navigate(`/agents/workflow/${agent.id}/copy`);
      }
    },
    [navigate, navigateWithBackHref]
  );

  const handleExportAgent = useCallback(
    async (agent: UnifiedAgent) => {
      setSelectedAgent(agent);
      setIsDialogOpen(true);
      setIsLoading(true);
      setExportData(null);

      try {
        let data;
        if (agent.agent_type === 'specialized') {
          data = await exportSpecializedAgent(instanceId || '', agent.id)();
        } else if (agent.agent_type === 'workflow') {
          data = await exportWorkflowAgent(instanceId || '', agent.id);
        }

        if (data) {
          setExportData(data);
          await copyJsonToClipboard(data);
          toaster.create('Agent JSON copied to clipboard', { intent: 'success' });
        }
      } catch (error) {
        console.error('Failed to export agent:', error);
        toaster.create('Failed to export agent', { intent: 'danger' });
        setIsDialogOpen(false);
      } finally {
        setIsLoading(false);
      }
    },
    [instanceId]
  );

  const handleOpenPublishSidebar = useCallback((agent: UnifiedAgent) => {
    setSelectedAgentForPublish(agent);
    setIsPublishSidebarOpen(true);
  }, []);

  const handleClosePublishSidebar = useCallback(() => {
    setIsPublishSidebarOpen(false);
    setSelectedAgentForPublish(null);
  }, []);

  const handlePublishSubmit = useCallback(
    (data: {
      is_featured: boolean;
      is_hidden: boolean;
      release_notes: string;
      tags: string[];
      etag?: string;
      details_url?: string;
    }) => {
      if (!selectedAgentForPublish) return;

      pushToDirectory({
        agentId: selectedAgentForPublish.agent_id,
        request: {
          agent_type: selectedAgentForPublish.agent_type,
          opal_instance_id: instanceId || '',
          publisher_email: opalUser.email,
          tags: data.tags,
          is_featured: data.is_featured,
          is_hidden: data.is_hidden,
          release_notes: data.release_notes,
          etag: data.etag,
          details_url: data.details_url,
        },
      });
      handleClosePublishSidebar();
    },
    [
      pushToDirectory,
      selectedAgentForPublish,
      instanceId,
      opalUser.email,
      handleClosePublishSidebar,
    ]
  );

  const agents = agentsResponse?.items || [];
  const totalCount = agentsResponse?.pagination?.total_count || 0;
  const totalPages = agentsResponse?.pagination?.total_pages || 0;

  if (error) {
    return (
      <Flex flexDirection="column" gap="16">
        <AgentSearchInput
          searchQuery={search}
          onSearchChange={handleSearchChange}
          agentType={type}
          status={status}
          onAgentTypeChange={handleAgentTypeChange}
          onStatusChange={handleStatusChange}
        />
        <EmptyPage
          title="Error loading agents"
          description="There was an error loading agents. Please try again."
        />
      </Flex>
    );
  }

  if (totalCount === 0 && !isPending) {
    return (
      <Flex flexDirection="column" gap="16">
        <AgentSearchInput
          searchQuery={search}
          onSearchChange={handleSearchChange}
          agentType={type}
          status={status}
          onAgentTypeChange={handleAgentTypeChange}
          onStatusChange={handleStatusChange}
        />
        <EmptyPage
          title="No agents found"
          description={
            search || type || status
              ? 'No agents match your search criteria. Try adjusting your filters.'
              : 'Create code-free AI agents to automate tasks and handle the heavy lifting for all your marketing needs.'
          }
          onClickPrimaryButton={() => navigate('/agents/specialized/create')}
          primaryButtonText="Add Agent"
        />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="16">
      <AgentSearchInput
        searchQuery={search}
        onSearchChange={handleSearchChange}
        agentType={type}
        status={status}
        onAgentTypeChange={handleAgentTypeChange}
        onStatusChange={handleStatusChange}
      />

      <AgentTabTable
        agents={agents}
        totalCount={totalCount}
        totalPages={totalPages}
        page={page}
        pageSize={pageSize}
        loading={isPending}
        onClickAgent={onClickAgentListItem}
        onCopyAgent={handleCopyAgent}
        onExportAgent={handleExportAgent}
        onPublishAgent={handleOpenPublishSidebar}
        showPublishOption={agentDirectoryPublisherFeature.enabled}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            {selectedAgent ? `Export: ${selectedAgent.name}` : 'Export Agent'}
          </DialogHeader>
          <DialogBody>
            {isLoading ? (
              <Flex justifyContent="center" alignItems="center" className={styles.loadingContainer}>
                <Spinner size="lg">Loading agent data...</Spinner>
              </Flex>
            ) : exportData ? (
              <Flex flexDirection="column" gap="16">
                <Text fontSize="sm" color="fg.secondary">
                  Agent JSON data:
                </Text>
                <MarkdownText
                  text={`\`\`\`json\n${JSON.stringify(exportData as object, null, 2)}\n\`\`\``}
                />
              </Flex>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button appearance="subtle" onClick={() => setIsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PublishAgentSidebar
        open={isPublishSidebarOpen}
        agent={selectedAgentForPublish}
        onClose={handleClosePublishSidebar}
        onPublish={handlePublishSubmit}
        isPublishing={isPushingToDirectory}
      />
    </Flex>
  );
}
